import classNames from 'classnames';
import React from 'react';

import {
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  Radio as MuiRadio,
  RadioGroup,
  Typography,
  makeStyles,
} from '@material-ui/core';

import LabelWithTooltip from './LabelWithTooltip/LabelWithTooltip.component';
import { useErrorStyles } from './form.styles';

const useStyles = makeStyles((theme) => ({
  input: {
    '& legend': {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',

      '&.Mui-focused': {
        color: 'initial',
      },
      '& .label--required': {
        color: theme.palette.primary.main,
      },
      '& .label--icon-valid': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiRadio-colorSecondary.Mui-checked': {
      color: theme.palette.primary.main,
    },
  },
  radioBtnWrapper: {
    position: 'relative',
  },
  additionalText: {
    position: 'absolute',
    left: '44px',
    bottom: '-8px',
    fontSize: '12px',
    width: 'max-content',
  },
}));

type Props = {
  label: string;
  tooltip?: string;
  iconTooltip?: React.ReactNode;
  menuItems: {
    label: string;
    value: string | number | boolean;
    additionalText?: string;
  }[];
  name: string;
  value?: string | number | boolean;
  error?: string;
  isErrorVisible?: boolean;
  required?: boolean;
  onFocus?: (event: React.ChangeEvent<HTMLElement>) => void;
  onBlur?: (event: React.ChangeEvent<HTMLElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLElement>) => void;
  innerRef?: React.MutableRefObject<HTMLInputElement | null>;
};

export const Radio: React.FC<Props> = (props) => {
  const classes = useStyles();
  const { errorFont, errorBlock } = useErrorStyles();
  const {
    label,
    tooltip,
    iconTooltip,
    menuItems,
    name,
    value,
    onChange,
    onFocus,
    onBlur,
    required = true,
    innerRef,
    isErrorVisible,
  } = props;
  const error = props.error && props?.error !== 'undefined' && props.error;

  function parseOptionValue(value: string): string | boolean {
    switch (value) {
      case 'true':
        return true;
      case 'false':
        return false;
    }
    return value;
  }

  return (
    <FormControl component="fieldset" className={classes.input}>
      <FormLabel component="legend">
        <LabelWithTooltip
          label={label}
          required={required}
          tooltip={tooltip}
          icon={iconTooltip}
        />
      </FormLabel>
      <FormGroup row={true}>
        <RadioGroup
          row={true}
          name={name}
          ref={innerRef}
          value={String(value)}
          onFocus={onFocus}
          onBlur={(event) => {
            const eventWithFullName = {
              ...event,
              target: {
                ...event.target,
                name,
              },
              currentTarget: {
                ...event.currentTarget,
                name,
              },
            } as unknown as React.ChangeEvent<HTMLInputElement>;
            onBlur?.(eventWithFullName);
          }}
          onChange={(event) => {
            const eventValue = event.target.value ?? event.target?.value;
            const optionValue = parseOptionValue(eventValue);
            const eventWithValue = {
              ...event,
              target: {
                ...event.target,
                name,
                value: optionValue,
              },
              currentTarget: {
                ...event.currentTarget,
                name,
                value: optionValue,
              },
            } as unknown as React.ChangeEvent<HTMLInputElement>;
            onChange?.(eventWithValue);
          }}
        >
          {menuItems.map((item) => (
            <FormControlLabel
              key={`${item.label}-${item.value}`}
              label={item.label}
              control={
                <Grid className={classes.radioBtnWrapper}>
                  <MuiRadio name={item.label} value={String(item.value)} />
                  {item.additionalText && (
                    <Typography className={classes.additionalText}>
                      {item.additionalText}
                    </Typography>
                  )}
                </Grid>
              }
            />
          ))}
        </RadioGroup>
      </FormGroup>
      {error && isErrorVisible && (
        <FormHelperText className={classNames(errorFont, errorBlock)}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
};
